import { useEffect as useEffectAs, useRef as useRefAs, useState as useStateAs } from "react";
import { Api } from "./api";
import { Banner, Field, Icon } from "./components";

// Neighbor assistance requests — community members asking for help.
// 1 OPEN per user per community. 14-day auto-expire.

const AS_CATEGORY_LABEL = {
  moving: "Moving / Lifting",
  transport: "Ride / Transport",
  repairs: "Quick repair",
  errand: "Errand",
  pet_help: "Pet help",
  child_help: "Childcare help",
  tech_help: "Tech help",
  medical: "Meals / Medical",
  other: "Other",
};
const AS_CATEGORY_ICON = {
  moving: "arrow", transport: "arrow", repairs: "settings", errand: "tag",
  pet_help: "spark", child_help: "users", tech_help: "settings",
  medical: "spark", other: "tag",
};
const AS_URGENCY_LABEL = { today: "Today", this_week: "This week", flexible: "Flexible" };
const AS_URGENCY_PILL = {
  today: { text: "Today", cls: "pill--red" },
  this_week: { text: "This week", cls: "pill--gold" },
  flexible: { text: "Flexible", cls: "pill--ghost" },
};
const AS_STATUS_PILL = {
  open: { text: "Open", cls: "pill--green" },
  resolved: { text: "Resolved", cls: "pill--ghost" },
  withdrawn: { text: "Withdrawn", cls: "pill--ghost" },
  expired: { text: "Expired", cls: "pill--ghost" },
};

function _relTimeAs(iso) {
  if (!iso) return "";
  const ms = Date.now() - new Date(iso).getTime();
  const sec = Math.round(ms / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 7) return `${day}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

// CODE_REVIEW P2 L266 — routes through the shared Api.authedBlobUrl cache.
function AssistImage({ imageId, alt = "", style }) {
  const [src, setSrc] = useStateAs(null);
  const [err, setErr] = useStateAs(false);
  useEffectAs(() => {
    if (!imageId) { setSrc(null); setErr(false); return; }
    let cancelled = false;
    let release = null;
    setErr(false);
    window.Api.authedBlobUrl(`/api/assistance/images/${imageId}`).then(
      ({ url, release: r }) => {
        if (cancelled) { r(); return; }
        release = r;
        setSrc(url);
      },
      () => { if (!cancelled) setErr(true); },
    );
    return () => { cancelled = true; if (release) release(); };
  }, [imageId]);
  if (err || !src) return null;
  return <img src={src} alt={alt} style={style} />;
}

function Assistance({ user, onMessageRequester }) {
  const [view, setView] = useStateAs("browse");
  const [activeId, setActiveId] = useStateAs(null);
  const open = (id) => { setActiveId(id); setView("detail"); };
  const openNew = () => { setActiveId(null); setView("new"); };
  const openEdit = (id) => { setActiveId(id); setView("edit"); };
  const back = () => { setView("browse"); setActiveId(null); };

  if (view === "detail" && activeId) {
    return <AssistDetail id={activeId} user={user} onBack={back}
      onEdit={() => openEdit(activeId)} onDeleted={back}
      onMessageRequester={onMessageRequester} />;
  }
  if (view === "new") return <AssistForm user={user} onCancel={back} onSaved={(r) => open(r.id)} />;
  if (view === "edit" && activeId) {
    return <AssistForm user={user} editingId={activeId}
      onCancel={() => open(activeId)} onSaved={() => open(activeId)} />;
  }
  return <AssistBrowse user={user} onOpen={open} onNew={openNew} />;
}

function AssistBrowse({ user, onOpen, onNew }) {
  const [state, setState] = useStateAs({ loading: true, error: null, requests: [], meta: null });
  const [usage, setUsage] = useStateAs(null);
  const [category, setCategory] = useStateAs("");
  const [urgency, setUrgency] = useStateAs("");
  const [mineOnly, setMineOnly] = useStateAs(false);
  const [hideClosed, setHideClosed] = useStateAs(true);
  const [q, setQ] = useStateAs("");
  // {limit, offset, total, has_more} pagination — CODE_REVIEW P2 L268.
  const [offset, setOffset] = useStateAs(0);
  // Request-invalidation counter — see marketplace.jsx for context
  // (codex P1 on PR #156). Older responses get dropped.
  const reqIdRef = useRefAs(0);

  const reload = (nextOffset = offset) => {
    const myReq = ++reqIdRef.current;
    setState((s) => ({ loading: true, error: null, requests: [], meta: s.meta }));
    window.Api.assistanceList({
      category: category || undefined,
      urgency: urgency || undefined,
      mine_only: mineOnly,
      status: hideClosed ? "open" : "open,resolved,withdrawn,expired",
      q: q || undefined,
      offset: nextOffset,
    })
      .then(({ items, meta }) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: null, requests: items || [], meta: meta || null });
      })
      .catch((err) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: err.message || "Couldn't load.", requests: [], meta: null });
      });
    window.Api.assistanceUsage().then(setUsage).catch(() => {});
  };
  useEffectAs(() => { setOffset(0); /* eslint-disable-next-line */ }, [category, urgency, mineOnly, hideClosed]);
  useEffectAs(() => { reload(offset); /* eslint-disable-next-line */ }, [category, urgency, mineOnly, hideClosed, offset]);
  useEffectAs(() => {
    const t = setTimeout(() => { setOffset(0); reload(0); }, 250);
    return () => clearTimeout(t);
    /* eslint-disable-next-line */
  }, [q]);

  const community = user?.community?.name || "your community";
  const capReached = usage && usage.open >= usage.open_limit;

  return (
    <div className="scroll">
      <div className="stack-md">
        <Banner icon="users" tone="accent">
          <b>{community} Assistance.</b> A focused place to ask neighbors for help —
          moving, rides, repairs, errands. One open request at a time so the section
          stays useful for everyone.
        </Banner>

        <div className="row" style={{ justifyContent: "space-between", flexWrap: "wrap", gap: 12 }}>
          <div className="card" style={{ padding: "8px 12px", flex: 1, display: "flex", alignItems: "center", gap: 10, minWidth: 260 }}>
            <Icon name="search" size={16} style={{ color: "var(--mute)" }} />
            <input className="input" aria-label="Search assistance requests" placeholder="Search requests…" value={q}
              onChange={(e) => setQ(e.target.value)}
              style={{ border: 0, padding: 0, background: "transparent", width: "100%" }} />
          </div>
          <button className="btn btn--primary" onClick={onNew} disabled={capReached}
            title={capReached ? "You already have an open request — resolve or withdraw it first." : undefined}>
            <Icon name="plus" size={16} /> &nbsp;Ask for help
          </button>
        </div>

        {usage && (
          <div className="card" style={{
            padding: "10px 14px", fontSize: 13,
            background: capReached ? "var(--accent-soft, #eef3e6)" : "var(--surface)",
            border: capReached ? "1px solid color-mix(in oklab, var(--accent, #7c8a6b) 22%, transparent)" : "1px solid var(--hairline)",
          }}>
            {capReached
              ? <>You have <b>1</b> open request. Resolve or withdraw it to post another.</>
              : <>You can post <b>1</b> open request at a time. Requests auto-expire after 14 days.</>}
          </div>
        )}

        <div className="row" style={{ flexWrap: "wrap", gap: 6 }}>
          <CategoryChipAs active={!category} onClick={() => setCategory("")} label="All" iconName="tag" />
          {Object.keys(AS_CATEGORY_LABEL).map((k) => (
            <CategoryChipAs key={k} active={category === k} onClick={() => setCategory(k)}
              label={AS_CATEGORY_LABEL[k]} iconName={AS_CATEGORY_ICON[k]} />
          ))}
        </div>

        <div className="row" style={{ gap: 16, fontSize: 13, color: "var(--mute)", flexWrap: "wrap" }}>
          <div className="row" style={{ gap: 6, alignItems: "center" }}>
            <span>Urgency:</span>
            <select className="select" value={urgency} onChange={(e) => setUrgency(e.target.value)} style={{ padding: "4px 8px", fontSize: 13 }}>
              <option value="">Any</option>
              {Object.entries(AS_URGENCY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          <label className="row" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={mineOnly} onChange={(e) => setMineOnly(e.target.checked)} />
            Only my posts
          </label>
          <label className="row" style={{ gap: 6, cursor: "pointer" }}>
            <input type="checkbox" checked={hideClosed} onChange={(e) => setHideClosed(e.target.checked)} />
            Hide resolved / closed
          </label>
        </div>

        {state.loading && (
          <div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div>
        )}
        {state.error && (
          <div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error}</div>
        )}
        {!state.loading && !state.error && state.requests.length === 0 && (
          <div className="card card--pad-lg" style={{ textAlign: "center" }}>
            <div style={{ color: "var(--mute)", marginBottom: 14 }}>
              {q || category || urgency || mineOnly
                ? "No requests match your filters."
                : "No open requests right now. Be the first to ask if you need a hand!"}
            </div>
            {!capReached && <button className="btn btn--primary" onClick={onNew}>Post a request</button>}
          </div>
        )}
        {!state.loading && state.requests.length > 0 && (
          <div className="stack-sm">
            {state.requests.map((r) => <AssistRow key={r.id} req={r} onClick={() => onOpen(r.id)} />)}
          </div>
        )}
        <window.Pager meta={state.meta} offset={offset} onChange={setOffset} busy={state.loading} />
      </div>
    </div>
  );
}

function CategoryChipAs({ active, onClick, label, iconName }) {
  return (
    <button type="button" onClick={onClick} style={{
      display: "inline-flex", alignItems: "center", gap: 6,
      padding: "6px 12px", borderRadius: 999,
      border: active ? "1px solid var(--accent)" : "1px solid var(--hairline)",
      background: active ? "var(--accent-soft)" : "var(--surface)",
      color: active ? "var(--accent-ink)" : "var(--ink)",
      fontSize: 13, fontWeight: active ? 500 : 400, cursor: "pointer",
    }}>
      <Icon name={iconName || "tag"} size={13} style={{ opacity: 0.7 }} />
      {label}
    </button>
  );
}

function AssistRow({ req, onClick }) {
  const urgencyPill = AS_URGENCY_PILL[req.urgency] || AS_URGENCY_PILL.flexible;
  const statusPill = AS_STATUS_PILL[req.status] || AS_STATUS_PILL.open;
  const firstImage = (req.images || [])[0];
  return (
    <button type="button" onClick={onClick} className="card" style={{
      display: "flex", gap: 14, padding: 14, textAlign: "left", cursor: "pointer",
      alignItems: "center", border: "1px solid var(--hairline)", background: "var(--surface)",
    }}
      onMouseEnter={(e) => { e.currentTarget.style.boxShadow = "var(--shadow-lg)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.boxShadow = ""; }}>
      <div style={{ width: 56, height: 56, borderRadius: 10, background: "var(--bg-2)",
        display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0, overflow: "hidden" }}>
        {firstImage
          ? <AssistImage imageId={firstImage.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
          : <Icon name={AS_CATEGORY_ICON[req.category] || "users"} size={24} style={{ color: "var(--mute)" }} />}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <span style={{ fontWeight: 600, fontSize: 15 }}>{req.title}</span>
          <span className={`pill ${urgencyPill.cls}`}>{urgencyPill.text}</span>
          {req.status !== "open" && <span className={`pill ${statusPill.cls}`}>{statusPill.text}</span>}
          {req.is_mine && <span className="pill pill--accent">Your post</span>}
        </div>
        {req.description && (
          <div style={{ fontSize: 13, color: "var(--mute)", marginTop: 4, overflow: "hidden", display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical" }}>
            {req.description}
          </div>
        )}
        <div className="row" style={{ gap: 6, fontSize: 12, color: "var(--mute)", marginTop: 6 }}>
          <span>{AS_CATEGORY_LABEL[req.category] || req.category}</span>
          <span>·</span>
          <span>{req.requester?.display_name || req.requester?.full_name || "Neighbor"}</span>
          <span>·</span>
          <span>{_relTimeAs(req.created_at)}</span>
        </div>
      </div>
    </button>
  );
}

function AssistDetail({ id, user, onBack, onEdit, onDeleted, onMessageRequester }) {
  const [state, setState] = useStateAs({ loading: true, error: null, request: null });
  const [busy, setBusy] = useStateAs(false);
  const [activeImage, setActiveImage] = useStateAs(0);

  const reload = () => {
    setState({ loading: true, error: null, request: null });
    window.Api.assistanceGet(id)
      .then((request) => setState({ loading: false, error: null, request }))
      .catch((err) => setState({ loading: false, error: err.message || "Couldn't load.", request: null }));
  };
  useEffectAs(() => { reload(); /* eslint-disable-next-line */ }, [id]);

  const setStatus = async (status) => {
    setBusy(true);
    try {
      const updated = await window.Api.assistanceUpdate(id, { status });
      setState({ loading: false, error: null, request: updated });
    } catch (err) { alert(err.message || "Couldn't update."); }
    finally { setBusy(false); }
  };
  const onDelete = async () => {
    if (!window.confirm("Delete this request? This cannot be undone.")) return;
    setBusy(true);
    try { await window.Api.assistanceDelete(id); onDeleted(); }
    catch (err) { alert(err.message || "Couldn't delete."); setBusy(false); }
  };

  if (state.loading) return <div className="scroll"><div className="card card--pad-lg" style={{ textAlign: "center", color: "var(--mute)" }}>Loading…</div></div>;
  if (state.error || !state.request) return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--danger)", textAlign: "center" }}>{state.error || "Not found."}</div></div>;

  const r = state.request;
  const urgencyPill = AS_URGENCY_PILL[r.urgency] || AS_URGENCY_PILL.flexible;
  const statusPill = AS_STATUS_PILL[r.status] || AS_STATUS_PILL.open;
  const requester = r.requester;
  const name = (requester.display_name || requester.full_name || "Neighbor").trim();

  return (
    <div className="scroll">
      <div className="stack-md" style={{ maxWidth: 820 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <button className="btn btn--ghost btn--sm" onClick={onBack}>
            <Icon name="chevron" size={14} style={{ transform: "rotate(180deg)" }} /> &nbsp;Back to assistance
          </button>
          {r.can_edit && (
            <div className="row" style={{ gap: 8 }}>
              <button className="btn btn--ghost btn--sm" onClick={onEdit} disabled={busy}>
                <Icon name="edit" size={14} /> &nbsp;Edit
              </button>
              <button className="btn btn--ghost btn--sm" onClick={onDelete} disabled={busy} style={{ color: "var(--danger)" }}>
                <Icon name="trash" size={14} /> &nbsp;Delete
              </button>
            </div>
          )}
        </div>

        <div>
          <div className="row" style={{ gap: 8, alignItems: "center", marginBottom: 6, flexWrap: "wrap" }}>
            <span className={`pill ${urgencyPill.cls}`}>{urgencyPill.text}</span>
            {r.status !== "open" && <span className={`pill ${statusPill.cls}`}>{statusPill.text}</span>}
            <span className="text-sm text-mute">{AS_CATEGORY_LABEL[r.category] || r.category}</span>
          </div>
          <h1 style={{ fontSize: 24, fontWeight: 600, margin: 0, lineHeight: 1.2 }}>{r.title}</h1>
          <div className="text-sm text-mute" style={{ marginTop: 6 }}>
            Asked by {name} · Posted {_relTimeAs(r.created_at)}
          </div>
        </div>

        {r.description && (
          <div style={{ whiteSpace: "pre-wrap", fontSize: 14, lineHeight: 1.6 }}>{r.description}</div>
        )}

        {r.images.length > 0 && (
          <div>
            <div style={{ aspectRatio: "4/3", maxHeight: 360, background: "var(--bg-2)", borderRadius: 12, overflow: "hidden" }}>
              <AssistImage imageId={r.images[activeImage]?.id} alt={r.title}
                style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }} />
            </div>
            {r.images.length > 1 && (
              <div className="row" style={{ gap: 8, marginTop: 10, flexWrap: "wrap" }}>
                {r.images.map((img, i) => (
                  <button key={img.id} type="button" onClick={() => setActiveImage(i)}
                    style={{ width: 56, height: 56, padding: 0, borderRadius: 8, overflow: "hidden",
                      border: i === activeImage ? "2px solid var(--accent)" : "1px solid var(--hairline)",
                      cursor: "pointer", background: "var(--bg-2)" }}>
                    <AssistImage imageId={img.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {!r.is_mine && r.status === "open" && (
          <div className="card" style={{ padding: 14 }}>
            <div style={{ fontWeight: 600, marginBottom: 4 }}>Can you help {name.split(" ")[0]}?</div>
            <div className="text-sm text-mute" style={{ marginBottom: 10 }}>
              Send a private message to offer help and work out details.
            </div>
            <button type="button" className="btn btn--primary"
              onClick={() => onMessageRequester && onMessageRequester({
                recipient_user_id: requester.id,
                recipient_name: name,
                related_assistance_id: r.id,
                listing_title: r.title,
              })}
              disabled={!onMessageRequester}>
              Message {name.split(" ")[0]}
            </button>
          </div>
        )}

        {r.is_mine && (
          <div className="card" style={{ padding: 14 }}>
            <div className="text-xs text-mute" style={{ marginBottom: 6, letterSpacing: 0.4, textTransform: "uppercase" }}>Status</div>
            <div className="row" style={{ gap: 8, flexWrap: "wrap" }}>
              {r.status !== "open" && (
                <button className="btn btn--ghost btn--sm" onClick={() => setStatus("open")} disabled={busy}>Reopen</button>
              )}
              {r.status === "open" && (
                <button className="btn btn--primary btn--sm" onClick={() => setStatus("resolved")} disabled={busy}>
                  <Icon name="check" size={14} />&nbsp; Mark resolved
                </button>
              )}
              {r.status === "open" && (
                <button className="btn btn--ghost btn--sm" onClick={() => setStatus("withdrawn")} disabled={busy}>Withdraw</button>
              )}
            </div>
          </div>
        )}

        {!r.is_mine && r.can_moderate && (
          <div className="card" style={{ padding: 14 }}>
            <div className="text-xs text-mute" style={{ marginBottom: 6 }}>Moderator</div>
            <button className="btn btn--ghost btn--sm" onClick={onDelete} style={{ color: "var(--danger)" }} disabled={busy}>
              Remove this request
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

const AS_MAX_IMAGES = 3;

function AssistForm({ user, editingId, onCancel, onSaved }) {
  const isEdit = !!editingId;
  const [loaded, setLoaded] = useStateAs(!isEdit);
  const [title, setTitle] = useStateAs("");
  const [description, setDescription] = useStateAs("");
  const [category, setCategory] = useStateAs("other");
  const [urgency, setUrgency] = useStateAs("flexible");
  const [images, setImages] = useStateAs([]);
  const [pendingFiles, setPendingFiles] = useStateAs([]);
  const [busy, setBusy] = useStateAs(false);
  const [error, setError] = useStateAs(null);
  const fileInputRef = useRefAs(null);

  // Revoke any blob URLs still attached when the form unmounts
  // (route change / cancel). Ref dance so the cleanup sees the latest
  // list without re-running on every change.
  const pendingFilesRef = useRefAs(pendingFiles);
  useEffectAs(() => { pendingFilesRef.current = pendingFiles; }, [pendingFiles]);
  useEffectAs(() => {
    return () => {
      pendingFilesRef.current.forEach((f) => {
        if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, []);

  useEffectAs(() => {
    if (!isEdit) return;
    window.Api.assistanceGet(editingId).then((r) => {
      setTitle(r.title); setDescription(r.description || "");
      setCategory(r.category); setUrgency(r.urgency);
      setImages(r.images || []); setLoaded(true);
    }).catch((err) => setError(err.message || "Couldn't load."));
  }, [editingId, isEdit]);

  // Wrap each picked File with a stable id + cached blob URL — see
  // marketplace.jsx for the same fix (codex P1 on #244): inline
  // URL.createObjectURL(f) on every render leaked blob URLs per
  // keystroke and index-keyed nodes showed the wrong photo after
  // removing a non-last entry.
  const onFilesPicked = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    const room = AS_MAX_IMAGES - images.length - pendingFiles.length;
    if (room <= 0) { setError(`Max ${AS_MAX_IMAGES} images per request.`); return; }
    const wrapped = files.slice(0, room).map((file) => ({
      id: (window.crypto?.randomUUID?.()) || `tmp-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    setPendingFiles((cur) => [...cur, ...wrapped]);
    e.target.value = "";
  };
  const onRemovePending = (id) => setPendingFiles((cur) => {
    const removed = cur.find((f) => f.id === id);
    if (removed?.previewUrl) URL.revokeObjectURL(removed.previewUrl);
    return cur.filter((f) => f.id !== id);
  });
  const onRemoveExisting = async (imageId) => {
    if (!window.confirm("Remove this image?")) return;
    try {
      await window.Api.assistanceDeleteImage(imageId);
      setImages((cur) => cur.filter((i) => i.id !== imageId));
    } catch (err) { setError(err.message || "Couldn't remove image."); }
  };

  const submit = async (e) => {
    e?.preventDefault?.();
    if (busy) return;
    setError(null);
    if (!title.trim()) { setError("Title is required."); return; }
    setBusy(true);
    try {
      const body = {
        title: title.trim(),
        description: description.trim() || undefined,
        category, urgency,
      };
      const saved = isEdit
        ? await window.Api.assistanceUpdate(editingId, body)
        : await window.Api.assistanceCreate(body);
      // Track failed uploads so the user gets a heads-up — pre-#251 we
      // silently dropped photos on flaky networks with no indication.
      const failedUploads = [];
      for (const wrapped of pendingFiles) {
        try { await window.Api.assistanceUploadImage(saved.id, wrapped.file); }
        catch (uploadErr) {
          console.warn("image upload failed:", uploadErr);
          failedUploads.push(wrapped);
        }
      }
      if (failedUploads.length > 0) {
        window.alert(
          `Saved, but ${failedUploads.length} photo${failedUploads.length === 1 ? "" : "s"} couldn't upload (likely a flaky connection). Open the request to add ${failedUploads.length === 1 ? "it" : "them"} again.`,
        );
      }
      onSaved(saved);
    } catch (err) { setError(err.message || "Couldn't save."); }
    finally { setBusy(false); }
  };

  if (isEdit && !loaded) {
    return <div className="scroll"><div className="card card--pad-lg" style={{ color: "var(--mute)", textAlign: "center" }}>Loading…</div></div>;
  }

  return (
    <div className="scroll">
      <form className="stack-md" onSubmit={submit} style={{ maxWidth: 700 }}>
        <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
          <h2 style={{ fontSize: 22, fontWeight: 600, margin: 0 }}>{isEdit ? "Edit request" : "Ask for help"}</h2>
          <button type="button" className="btn btn--ghost btn--sm" onClick={onCancel}>Cancel</button>
        </div>

        {error && (
          <div role="alert" style={{ padding: "10px 12px", borderRadius: 8, background: "var(--danger-soft, #fdecea)", color: "var(--danger, #b3261e)" }}>
            {error}
          </div>
        )}

        <div className="card card--pad-lg" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Field label="What do you need?" hint="Keep it short. Neighbors with skim quickly.">
            <input className="input" value={title} onChange={(e) => setTitle(e.target.value)}
              placeholder="Need help moving a couch this Saturday" maxLength={140} />
          </Field>

          <Field label="Details" hint="When, where, and any specifics. The more clear, the more likely you'll get help.">
            <textarea className="input" rows={5} value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Mid-century couch from a 2nd-floor walk-up to my driveway Saturday morning. Two strong neighbors + 30 minutes. Happy to provide pizza."
              style={{ resize: "vertical", lineHeight: 1.5 }} />
          </Field>

          <div className="auth__form-row" style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <Field label="Category">
              <select className="select" value={category} onChange={(e) => setCategory(e.target.value)}>
                {Object.entries(AS_CATEGORY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
            <Field label="Urgency">
              <select className="select" value={urgency} onChange={(e) => setUrgency(e.target.value)}>
                {Object.entries(AS_URGENCY_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
              </select>
            </Field>
          </div>
        </div>

        <div className="card card--pad-lg">
          <div className="row" style={{ justifyContent: "space-between", alignItems: "center" }}>
            <div>
              <div style={{ fontWeight: 500 }}>Photos</div>
              <div className="text-xs text-mute">Optional. Up to {AS_MAX_IMAGES} — a picture helps a lot for repairs etc.</div>
            </div>
            <button type="button" className="btn btn--ghost btn--sm" onClick={() => fileInputRef.current?.click()}
              disabled={images.length + pendingFiles.length >= AS_MAX_IMAGES}>
              <Icon name="upload" size={14} />&nbsp; Add images
            </button>
            <input ref={fileInputRef} type="file" accept="image/*" multiple hidden onChange={onFilesPicked} />
          </div>
          {(images.length > 0 || pendingFiles.length > 0) && (
            <div className="row" style={{ gap: 10, marginTop: 12, flexWrap: "wrap" }}>
              {images.map((img) => (
                <div key={img.id} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px solid var(--hairline)" }}>
                  <AssistImage imageId={img.id} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button type="button" onClick={() => onRemoveExisting(img.id)}
                    title="Remove image" aria-label="Remove image"
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}>×</button>
                </div>
              ))}
              {pendingFiles.map((f) => (
                <div key={f.id} style={{ position: "relative", width: 88, height: 88, borderRadius: 8, overflow: "hidden", border: "1px dashed var(--accent)" }}>
                  <img src={f.previewUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
                  <button type="button" onClick={() => onRemovePending(f.id)}
                    title="Remove image" aria-label="Remove pending image"
                    style={{ position: "absolute", top: 4, right: 4, width: 22, height: 22, borderRadius: "50%", background: "rgba(0,0,0,0.6)", color: "#fff", border: 0, fontSize: 12, cursor: "pointer" }}>×</button>
                  <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, background: "rgba(0,0,0,0.55)", color: "#fff", fontSize: 10, textAlign: "center", padding: "2px 0" }}>will upload</div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="row" style={{ justifyContent: "flex-end", gap: 8 }}>
          <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={busy}>Cancel</button>
          <button type="submit" className="btn btn--primary" disabled={busy}>
            {busy ? "Saving…" : (isEdit ? "Save changes" : "Post request")}
          </button>
        </div>
      </form>
    </div>
  );
}

export { Assistance };
